import { DataTable } from '@app/components/data-table/data-table';
import { useLocalDataTable } from '@app/components/data-table/hooks/use-local-data-table';
import { ProductTransportType } from '@shared/api/types';
import { Button } from '@shared/components/ui/button';
import { useState } from 'react';
import ProductDetailTransportSheet from './product-detail-transport-sheet';
import { useProductTransportTableColumns } from './product-detail-transport-table-columns';

const PAGE_SIZE = 10;

function TransportDataTable({
  data,
  maxEmissions,
  onRowClick,
}: {
  data: ProductTransportType[];
  maxEmissions: number;
  onRowClick: (row: ProductTransportType | undefined) => void;
}) {
  const columns = useProductTransportTableColumns(maxEmissions);
  const { table } = useLocalDataTable({
    data,
    columns,
    getRowId: (row: ProductTransportType) => row.name,
    initialState: { sorting: [{ id: 'emissions', desc: true }] },
  });
  return (
    <DataTable
      table={table}
      pagination={false}
      className="bg-gray-00 text-left shadow-sm"
      tableClassName="table-fixed"
      columnClassName={{ emissions: 'border-r', bar: 'pl-0 mr-0' }}
      onRowClick={(row) => onRowClick(row?.original)}
      rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
    />
  );
}

interface ProductTransportTableProps {
  transportTypes: ProductTransportType[];
  maxEmissions: number;
  totalEmissions?: number;
  product?: string;
}

export default function ProductTransportTable({
  transportTypes,
  maxEmissions,
  totalEmissions,
  product,
}: ProductTransportTableProps) {
  const [showAll, setShowAll] = useState(false);
  const [selectedTransportType, setSelectedTransportType] =
    useState<ProductTransportType>();

  const visibleTypes = showAll
    ? transportTypes
    : transportTypes.slice(0, PAGE_SIZE);

  return (
    <div>
      <TransportDataTable
        key={visibleTypes.length}
        data={visibleTypes}
        maxEmissions={maxEmissions}
        onRowClick={setSelectedTransportType}
      />
      {transportTypes.length > PAGE_SIZE && (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? 'Show less'
            : `Show ${transportTypes.length - PAGE_SIZE} more`}
        </Button>
      )}
      {selectedTransportType && totalEmissions != null && product && (
        <ProductDetailTransportSheet
          product={product}
          isOpen={!!selectedTransportType}
          setIsOpen={(open) => {
            if (!open) setSelectedTransportType(undefined);
          }}
          transportType={selectedTransportType}
          totalEmissions={totalEmissions}
        />
      )}
    </div>
  );
}
